import { Fragment, type ReactNode } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { ProviderRun } from '@/lib/packshots';
import { STAGE_LABEL, type StageId } from './railModel';

type Props = {
  runs: readonly ProviderRun[];
};

export default function ModelLogs({ runs }: Props) {
  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="logs" className="border-b-0">
        <AccordionTrigger className="py-2 text-sm [&>svg]:hidden">Logs</AccordionTrigger>
        <AccordionContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No model calls yet.</p>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {runs.map((run, index) => (
                <AccordionItem key={`${run.startedAt}-${index}`} value={String(index)} className="rounded-md border px-3">
                  <AccordionTrigger className="py-2 text-left text-sm hover:no-underline">
                    <span className="min-w-0">
                      <span className="font-medium">{stageTitle(run.stage)}</span>
                      <span className="mt-0.5 block truncate text-xs font-normal text-muted-foreground">
                        {run.slug} · {run.status} · {seconds(run.durationMs)}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-3">
                    {run.exchanges && run.exchanges.length > 0 ? (
                      run.exchanges.map((exchange, exchangeIndex) => (
                        <div key={exchangeIndex} className="space-y-2">
                          {run.exchanges && run.exchanges.length > 1 && (
                            <p className="text-xs font-medium text-muted-foreground">Call {exchangeIndex + 1}</p>
                          )}
                          <LogBlock label="Request" value={exchange.request} />
                          <LogBlock label="Response" value={exchange.response} />
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">Request and response were not stored for this call.</p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function LogBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs leading-5">
        <code className="block font-mono whitespace-pre">{highlightJson(formatLog(value))}</code>
      </pre>
    </div>
  );
}

function stageTitle(stage: string): string {
  if (isStageId(stage)) return STAGE_LABEL[stage];
  return stage;
}

function isStageId(stage: string): stage is StageId {
  return stage in STAGE_LABEL;
}

function seconds(durationMs: number): string {
  return `${(durationMs / 1000).toFixed(1)}s`;
}

function formatLog(value: unknown): string {
  const expanded = expandJsonStrings(value);
  if (typeof expanded === 'string') return expanded;
  try {
    return JSON.stringify(expanded, null, 2) ?? '';
  } catch {
    return '';
  }
}

function expandJsonStrings(value: unknown): unknown {
  if (typeof value === 'string') return parseJsonText(value) ?? value;
  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === 'string')) {
      const joined = value.join('');
      const parsed = parseJsonText(joined);
      if (parsed !== null) return parsed;
      return joined;
    }
    return value.map((item) => expandJsonStrings(item));
  }
  if (!value || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) out[key] = expandJsonStrings(child);
  return out;
}

function parseJsonText(value: string): unknown | null {
  const trimmed = value.trim();
  const wrapped = (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));
  if (!wrapped) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return expandJsonStrings(parsed);
  } catch {
    return null;
  }
}

function highlightJson(source: string): ReactNode {
  const nodes: ReactNode[] = [];
  const token = /"(?:\\.|[^"\\])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;
  let last = 0;
  let index = 0;
  let match: RegExpExecArray | null = token.exec(source);
  while (match) {
    const start = match.index;
    if (start > last) nodes.push(<Fragment key={index}>{source.slice(last, start)}</Fragment>);
    index += 1;
    const text = match[0];
    if (text.startsWith('"')) {
      const keyCut = text.lastIndexOf(':');
      const isKey = keyCut > 0 && text.slice(keyCut).trim() === ':';
      if (isKey) {
        nodes.push(<span key={index} className="text-sky-700 dark:text-sky-300">{text.slice(0, keyCut)}</span>);
        index += 1;
        nodes.push(<Fragment key={index}>{text.slice(keyCut)}</Fragment>);
      } else {
        nodes.push(<span key={index} className="text-emerald-800 dark:text-emerald-300">{showEscapes(text)}</span>);
      }
    } else if (text === 'true' || text === 'false' || text === 'null') {
      nodes.push(<span key={index} className="text-violet-700 dark:text-violet-300">{text}</span>);
    } else {
      nodes.push(<span key={index} className="text-amber-700 dark:text-amber-300">{text}</span>);
    }
    index += 1;
    last = start + text.length;
    match = token.exec(source);
  }
  if (last < source.length) nodes.push(<Fragment key={index}>{source.slice(last)}</Fragment>);
  return nodes;
}

function showEscapes(token: string): string {
  return token.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '');
}
